import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm, useFormContext, useWatch } from "react-hook-form";
import * as z from "zod";
import { cn } from "@/lib/utils";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bookFlight, getSearchFilters } from "../actions/flightAction";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import OriginDestinationItem from "../components/search/OriginDestinationItem";

import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import PricingInfo from "../components/PricingInfo";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { HiArrowSmallLeft, HiOutlineArrowLongRight } from "react-icons/hi2";
import { FaUserLarge } from "react-icons/fa6";
import PassengerDetailsForm from "../components/PassengerDetailsForm";
import { format } from "date-fns";
import { isEmpty } from "../helpers/commonHelper";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Loading from "../common/Loading";
import Counter from "../common/Counter";
import { INTERNATIONAL } from "../constants/flightTypes";
export const phoneRegex = new RegExp(
  /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/
);

const documentSchema = z
  .object({
    expDate: z.date().optional(),
    number: z.string().optional(),
    docType: z.string().optional(),
    type: z.string().optional(),
    issueCountry: z.string().optional(),

  })
  .superRefine((doc, ctx) => {
    // consider these as "entered" fields
    const hasAny =
      !!doc.expDate ||
      (doc.number?.trim() ?? "") !== "" ||
      (doc.docType?.trim() ?? "N") !== "N" ||
      (doc.issueCountry?.trim() ?? "NP") !== "NP"

    if (!hasAny) return; // ✅ document left blank => OK

    // If any field is entered, require the full set (except "type" which stays optional)
    if (!doc.expDate) {
      ctx.addIssue({ code: "custom", path: ["expDate"], message: "Document Expiry date is required." });
    }
    if (!(doc.number?.trim())) {
      ctx.addIssue({ code: "custom", path: ["number"], message: "Document number is required" });
    }
    if (!(doc.docType?.trim())) {
      ctx.addIssue({ code: "custom", path: ["docType"], message: "Document type is required" });
    }

  });

const mainFormSchema = z.object({
  inventoryID: z.any(),
  supplierID: z.any(),
  fareBookingKey: z.any(),
  sessionID: z.any(),
  paxDetails: z.array(
    z.object({
      title: z.string().min(1, { message: "Title is required" }),
      firstName: z.string().min(1, { message: "First name is required" }),
      // middleName: z.string().optional(),
      lastName: z.string().min(1, { message: "Last name is required" }),
      contactNo: z
        .string()
        .min(1, { message: "Contact number is required" })
        .min(10, { message: "Contact number must be 10 digits" })
        .max(10, { message: "Contact number must be less than 10 digits" })
        .regex(phoneRegex, "Invalid contact number!"),
      gender: z.any(),
      email: z
        .string()
        .min(1, { message: "Email is required" })
        .email({ message: "Invalid Email" }),
      paxType: z.any(),
      nationality: z.any(),
      nameNumber: z.any(),
      passengerID: z.any(),
      paxRemarks: z.any(),
      isContactPerson: z.any(),
      dateOfBirth: z.date({
        required_error: "A date of birth is required.",
      }),
      document: documentSchema.optional(),
      services: z.array(
        z.object({
          serviceCode: z.any(),
          serviceType: z.any(),
        })
      ).optional(),
      isContactPerson: z.boolean().optional(),
    })
  ),
  contact: z.object({
    title: z.string().min(1, { message: "Title is required" }),
    firstName: z.string().min(1, { message: "First name is required" }),
    // middleName: z.any(),
    lastName: z.string().min(1, { message: "Last name is required" }),
    contactNo: z
      .string()
      .min(1, { message: "Contact number is required" })
      .min(10, { message: "Contact number must be 10 digits" })
      .max(10, { message: "Contact number must be less than 10 digits" })
      .regex(phoneRegex, "Invalid contact number!"),
    email: z
      .string()
      .min(1, { message: "Email is required" })
      .email({ message: "Invalid Email" }),
    paxType: z.any(),
    nationality: z.any(),
    nameNumber: z.any(),
  })
});

export default function ReviewFlightPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    holdedFlight,
    holdedFlightResponse,
    flightSearchForm,
    searchForm,
    searchFilters,
  } = useSelector((state) => state.flights);

  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [accordianValue, setAccordianValue] = useState("item-1");
  const [selectedPax, setSelectedPax] = useState({})
  if (flightSearchForm == null) {
    navigate("/flights");
    return;
  }

  // 1. Define your form.
  const form = useForm({
    resolver: zodResolver(mainFormSchema),
    defaultValues: {
      contact: { document: { docType: "N" } },
      paxDetails: [],
      sessionID: holdedFlightResponse.SessionID,
      inventoryID: holdedFlight.InventoryID,
      supplierID: holdedFlightResponse.Supplier,
      fareBookingKey: holdedFlight.FareOptions.BookingKey
    },
  });

  const handleTimeUp = () => {
    navigate("/flights");
    toast({
      variant: "destructive",
      title: "Time Exceed !!",
      description: "You took too long, book again quicker.",
    });
  };

  const handleAccordianChange = (e) => {
    setAccordianValue(e);
  };

  const { replace: replacePaxDetails, append: appendPaxDetails } = useFieldArray({
    control: form.control,
    name: "paxDetails",
  })
  useEffect(() => {

    const fareBreakdowns = holdedFlightResponse?.Fare?.FareBreakDowns ?? [];

    let runningIndex = 1;

    const passengerArray = fareBreakdowns.flatMap((b) => {
      const qty = Number(b?.Passenger?.QTY ?? 0);
      const code = String(b?.Passenger?.Code ?? "").toUpperCase();

      if (!code || qty <= 0) return [];

      return Array.from({ length: qty }, () => ({
        index: runningIndex++,
        code: code,
      }));
    });
    console.log("passengerArray", passengerArray)
    passengerArray.map((p, index) => (
      index == 0 ? replacePaxDetails({ nationality: "NP", paxType: p.code, document: { docType: "N", issueCountry: "NP" } }) :
        appendPaxDetails({ nationality: "NP", paxType: p.code, document: { docType: "N", issueCountry: "NP" } })

    )

    )
  }, [holdedFlightResponse])
  return (
    isSubmitting ? <Loading label="Processing... Please wait!" /> :
      Object.keys(holdedFlightResponse).length > 0 && (
        <>
          <div className="shadow sticky top-0 bg-background z-10">
            {!isDesktop && (
              <div className="lg:hidden block container mx-auto p-2 pb-3">
                <div className="flex items-center gap-x-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full p-2"
                    onClick={() => navigate('/flights')}
                  >
                    <HiArrowSmallLeft className="h-5 w-5" />
                  </Button>
                  <div className="flex-1">
                    <div className="flex items-center text-md gap-x-2">
                      <span className="font-medium">
                        {holdedFlightResponse.Origin}
                      </span>
                      <HiOutlineArrowLongRight />
                      <span className="font-medium">
                        {" "}
                        {holdedFlightResponse.Destination}
                      </span>
                    </div>
                    <div className="flex items-center text-2xs gap-x-1 text-muted-foreground">
                      <span>{format(searchForm.departure, "do LLL")} </span>
                      <span className="block">•</span>
                      <span className="flex items-center gap-2">
                        <span>
                          {searchForm.noOfPassenger +
                            searchForm.child +
                            searchForm.infant}
                        </span>
                        <FaUserLarge className="w-2 h-2" />
                      </span>
                      <span className="block">•</span>
                      <span>{searchForm.cabinClass}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className=" bg-gray-100 dark:bg-background">
            <div className="container m-auto p-0 pb-20 lg:p-4 lg:pb-4">
              <div className="bg-orange-100 border-orange-200 border text-orange-900 p-2 text-center mb-2 rounded">
                Flight availability is limited. Book within{" "}
                <span className="font-medium">
                  <Counter
                    timeLimit={holdedFlightResponse.TimeLimit}
                    handleTimeUp={handleTimeUp}
                  />
                </span>{" "}
                to secure your seat.
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 lg:col-span-8 bg-background rounded-lg shadow">
                  <div>
                    <Form {...form}>
                      <form>
                        <Accordion
                          type="single"
                          collapsible
                          defaultValue={accordianValue}
                          value={accordianValue}
                          onValueChange={handleAccordianChange}
                        >
                          <AccordionItem
                            value="item-1"
                          >
                            <AccordionTrigger className="px-4 pt-2 hover:bg-slate-100 hover:no-underline">
                              <div className="flex items-center">
                                <span className={cn(
                                  accordianValue === "item-1" &&
                                  "bg-gray-800 text-primary-foreground bottom-0",
                                  "rounded-full flex items-center justify-center h-10 w-10 me-3 border"
                                )}>
                                  1
                                </span>
                                <span className={accordianValue === "item-1" && "font-semibold"}>Review your itinerary</span>
                              </div>
                            </AccordionTrigger>

                            <AccordionContent>
                              <>
                                {holdedFlightResponse &&
                                  holdedFlight &&
                                  searchFilters &&
                                  holdedFlight.OriginDest.map((od, i) => {
                                    return (
                                      <OriginDestinationItem
                                        key={"key" + i}
                                        od={od}
                                        i={1}
                                        countryList={searchFilters.Airports}
                                        flightResp={holdedFlightResponse}
                                      />
                                    );
                                  })}
                                {holdedFlightResponse.TransitMessage && holdedFlightResponse.TransitMessage.length > 0 && (
                                  <div className="border rounded p-4 mx-4 lg:flex ">
                                    <AiOutlineInfoCircle className="me-3 text-sm mb-2" size={24} />
                                    <div className="flex-1">
                                      <h4 className="font-semibold">Transit Message</h4>
                                      {holdedFlightResponse.TransitMessage.map(tm => (
                                        <p>
                                          {tm.Message}
                                        </p>
                                      ))
                                      }
                                    </div>
                                  </div>
                                )}
                              </>
                            </AccordionContent>
                          </AccordionItem>

                          <AccordionItem
                            value="item-2"
                          >
                            <AccordionTrigger className="p-4 hover:bg-slate-100 hover:no-underline">
                              <div className="flex items-center">
                                <span className={cn(
                                  form.formState.isValid ? 'bg-primary' :
                                    accordianValue === "item-2" &&
                                    "bg-gray-800 text-primary-foreground bottom-0",
                                  "rounded-full flex items-center justify-center h-10 w-10 me-3 border"
                                )}>
                                  2
                                </span>
                                <span className={accordianValue === "item-2" && "font-semibold"}>Passenger Details</span>
                              </div>
                            </AccordionTrigger>

                            <AccordionContent>


                              <div className="border-b  last-of-type:border-0 mb-4">
                                <PassengerDetailsForm
                                  searchFilters={searchFilters}
                                />
                              </div>

                              <div className="flex flex-row-reverse px-6">
                                <Button
                                  type="button"
                                  onClick={() => {
                                    form.trigger('paxDetails').then((isValid) => {
                                      debugger;
                                      console.log("error", form.getFieldState('paxDetails').error);
                                      if (isValid) {
                                        setAccordianValue("item-3");
                                      }
                                    });
                                  }}
                                >
                                  Continue
                                </Button>
                              </div>
                            </AccordionContent>
                          </AccordionItem>

                          <AccordionItem
                            value="item-3"
                          >
                            <AccordionTrigger className="p-4 hover:bg-slate-100 hover:no-underline">
                              <div className="flex items-center">
                                <span className={cn(
                                  accordianValue === "item-3" &&
                                  "bg-gray-800 text-primary-foreground bottom-0",
                                  "rounded-full flex items-center justify-center h-10 w-10 me-3 border"
                                )}>
                                  3
                                </span>
                                <span className={accordianValue === "item-3" && "font-semibold"}>Add Contact Details</span>
                              </div>
                            </AccordionTrigger>

                            <ContactPersonForm />
                          </AccordionItem>
                        </Accordion>
                      </form>
                    </Form>
                  </div>
                </div>
                {isDesktop ? (
                  <div className="col-span-12 lg:col-span-4">
                    <PricingInfo info={holdedFlightResponse} />
                  </div>
                ) : (
                  ""
                )}
              </div>
            </div>

            {!isDesktop && (
              <Drawer>
                <div className="flex justify-between py-2 px-4 bg-background shadow-lg fixed bottom-0 left-0 w-full border-t">
                  <DrawerTrigger asChild>
                    <div className="cursor-pointer">
                      <h3>{holdedFlightResponse.Fare.GTotalFare}</h3>
                      <p className="text-primary text-xs">View Fare Breakdown</p>
                    </div>
                  </DrawerTrigger>
                  {/* <Button>Continue</Button> */}
                </div>
                <DrawerContent>
                  <PricingInfo info={holdedFlightResponse} />
                </DrawerContent>
              </Drawer>
            )}
          </div >
        </>
      )
  );
}
function ContactPersonForm() {
  const { control, handleSubmit, formState: { isSubmitting }, setValue } = useFormContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const paxDetails = useWatch({
    control,
    name: "paxDetails",
  })
  const selectedContact = paxDetails?.find(
    (pax) => pax.isContactPerson === true
  )
  useEffect(() => {
    if (!selectedContact) return

    setValue("contact", {
      title: selectedContact.title,
      firstName: selectedContact.firstName,
      lastName: selectedContact.lastName,
      contactNo: selectedContact.contactNo,
      email: selectedContact.email,
      paxType: "ADT",
      nationality: flightSearchForm?.nationality
    }, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    })
  }, [selectedContact, setValue])
  const {
    flightSearchForm,
  } = useSelector((state) => state.flights);


  const onSubmit = (e) => {
    e.FareType = flightSearchForm?.fareType
    window.onbeforeunload = null;
    e.paxDetails.forEach((pd, i) => {
      var finalServices = [];
      if (pd.document?.docType === "N") {
        pd.document = null;
      }
      pd.services?.forEach((ser) => {
        if (!isEmpty(ser.ServiceCode)) {
          finalServices.push(ser);
        }
      }) ?? [];
      pd.services = finalServices;
    });
    window.scroll(0, 0);
    dispatch(
      bookFlight(e, (resp) => {
        if (resp.IsSuccess) {
          localStorage.setItem("bookinfRef", resp.BookingRef);
          localStorage.setItem('currentFlightType', INTERNATIONAL);
          navigate("/payment");
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: resp.Message,
          });
        }
      })
    );
  };
  return <AccordionContent>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 justify-between p-6">
      <FormField
        control={control}
        name={`contact.title`}
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Title</FormLabel>
            <Select
              defaultValue={"Mr"}
              onValueChange={field.onChange}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select Title" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Mr">Mr</SelectItem>
                <SelectItem value="Miss">
                  Miss
                </SelectItem>
                <SelectItem value="Mrs">
                  Mrs
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )} />
      <FormField
        control={control}
        name="contact.firstName"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>First Name</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />

      <FormField
        control={control}
        name="contact.lastName"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Last Name</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />

      <FormField
        control={control}
        name="contact.contactNo"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Contact No</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />
      <FormField
        control={control}
        name="contact.email"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input
                type="email" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />
    </div>
    <div className="flex flex-row-reverse px-6 pt-4 pb-1 border-t">
      <Button type="button" onClick={handleSubmit(onSubmit)}>Submit</Button>
    </div>
  </AccordionContent>;
}

